import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { loadScript } from '@treatwell/ui';
import { OneChat, OneChatConfig } from './types';

type Props = {
  bootstrapJsSrc: string;
  configuration: OneChatConfig;
  initializeChatOnRender?: boolean;
};

export const useSupportChat = ({
  bootstrapJsSrc,
  configuration: {
    developerName,
    orgId,
    baseUrl,
    userInfo,
    uiConfig,
    getAccessToken,
    getProfileAccessToken,
    locale,
  },
  initializeChatOnRender = true,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const oneChatInstance = useRef<OneChat | null>(null);

  const initialize = useCallback(async (): Promise<OneChat | null> => {
    if (oneChatInstance.current) {
      return oneChatInstance.current;
    }
    setIsLoading(true);
    setIsReady(false);
    setError(null);
    try {
      await loadScript({ src: bootstrapJsSrc });
      if (window.OneChatApp && !window.OneChat) {
        const chatInstance = window.OneChatApp({
          developerName,
          orgId,
          baseUrl,
          locale: userInfo?.language || locale,
          getAccessToken,
          getProfileAccessToken,
          uiConfig,
          userInfo,
        });
        await chatInstance.init();
        oneChatInstance.current = chatInstance;
        setIsReady(true);
        return chatInstance;
      }
    } catch (err) {
      console.error(`Error initializing OneChat: ${err}`);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [
    bootstrapJsSrc,
    developerName,
    orgId,
    baseUrl,
    userInfo,
    locale,
    getAccessToken,
    getProfileAccessToken,
    uiConfig,
  ]);

  const openChat = useCallback(async () => {
    // As we might have not initialized the chat yet, we need to initialize it
    if (!oneChatInstance.current) {
      await initialize();
    }
    if (oneChatInstance.current) {
      oneChatInstance.current.open();
    } else {
      console.warn('OneChat instance initialized but undefined');
    }
  }, [initialize]);

  const closeChat = useCallback(() => {
    oneChatInstance?.current?.close();
  }, [oneChatInstance]);

  useEffect(() => {
    if (initializeChatOnRender && !oneChatInstance.current) {
      initialize();
    }
  }, [initialize, initializeChatOnRender]);

  return useMemo(
    () => ({
      openChat,
      closeChat,
      isLoading,
      isReady,
      error,
    }),
    [openChat, closeChat, isLoading, isReady, error]
  );
};
